import {
    type ColDef,
    type SelectionChangedEvent,
} from '@ag-grid-community/core';
import { type ReactNode, useMemo, useState } from 'react';
import { GET_COMPONENTS_SERVER } from '@api/queries/components';
import { ICSAgGrid } from '@components/ICSGrid/ICSAgGrid';
import { TableHeader } from '@components/NewComponentCreateRequestTrigger/components/SearchComponentsDialog/components/TableHeader';
import {
    ComponentOrderField,
    type Component,
    type ComponentWhereInput,
    type GetComponentsServerQuery,
} from '@gql/graphql';
import { useDebounce } from '@hooks/useDebounce';
import {
    Datasource,
    type GraphQLDataSourceOptions,
    GraphQLDataSourceStrategy,
} from '@utils/ag-datasource/ag-datasource';
import { flatMapEdges } from '@utils/flatMapEdges';

interface SearchComponentsTableProps {
    onSelectionChanged: (event: SelectionChangedEvent<Component>) => void;
    headerSuffix?: ReactNode;
}

const BLOCK_SIZE = 100;

const tableColumns: ColDef<Component>[] = [
    {
        field: 'componentVendor.name',
        headerName: 'Vendor',
        filter: false,
        floatingFilter: false,
    },
    { field: 'name', filter: false, floatingFilter: false },
    { field: 'version', filter: false, floatingFilter: false },
];

export function SearchComponentsTable(
    props: SearchComponentsTableProps,
): ReactNode {
    const { headerSuffix, onSelectionChanged } = props;

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const datasource = useMemo(() => {
        const where = getComponentSearchWhere(debouncedSearchTerm);
        const options: GraphQLDataSourceOptions = {
            query: GET_COMPONENTS_SERVER,
            variables: where ? { where } : undefined,
            infiniteScroll: true,
            columnSortMap: (colId) => {
                switch (colId) {
                    case 'version':
                        return ComponentOrderField.Version;
                    case 'name':
                    default:
                        return ComponentOrderField.Name;
                }
            },
        };

        const strategy =
            new GraphQLDataSourceStrategy<GetComponentsServerQuery>(
                options,
                ({ data: { components } }) => ({
                    rows: flatMapEdges(components),
                    totalCount: components.totalCount,
                }),
            );

        return new Datasource(strategy);
    }, [debouncedSearchTerm]);

    return (
        <>
            <TableHeader
                searchTerm={searchTerm}
                searchTermChange={setSearchTerm}
                headerSuffix={headerSuffix}
            />

            <ICSAgGrid<Component>
                wrapperClassName="w-full"
                tableDescription="Component selection table"
                columnDefs={tableColumns}
                serverSideDatasource={datasource}
                rowSelection="single"
                onSelectionChanged={onSelectionChanged}
                domLayout="normal"
                suppressMenuColumn
                hideSearch
                pagination={false}
                cacheBlockSize={BLOCK_SIZE}
            />
        </>
    );
}

function getComponentSearchWhere(
    searchTerm: string,
): ComponentWhereInput | undefined {
    const search = searchTerm.trim();
    if (!search) return undefined;

    return {
        or: [
            { nameContainsFold: search },
            { hasComponentVendorWith: [{ nameContainsFold: search }] },
        ],
    };
}
